import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPlay, faStar, faFilm, faTv, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Header, PageLoader } from './shared';
import { IMAGE_SIZES, VIDEO_SOURCE_URL } from '../config/constants';
import { useDevice } from '../hooks/useMediaQuery';
import type { MediaItem } from '../types';
import { getMediaTitle, getMediaYear } from '../utils';

const ActorDetails: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  useDevice(); // For future responsive enhancements
  const { films = [] } = (location.state as { films?: MediaItem[] }) || {};

  const [selectedFilm, setSelectedFilm] = useState<MediaItem | null>(null);
  const [filter, setFilter] = useState<'all' | 'movie' | 'tv'>('all'); // 'all', 'movie', 'tv'

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // If no films data, redirect back
  useEffect(() => {
    if (!films || films.length === 0) {
      navigate(-1);
    }
  }, [films, navigate]);

  const handleFilmSelect = (film: MediaItem) => {
    setSelectedFilm(film);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filteredFilms = (films || []).filter((film: MediaItem) => {
    if (filter === 'all') return true;
    return film.media_type === filter;
  }).sort((a: MediaItem, b: MediaItem) => (b.vote_average || 0) - (a.vote_average || 0));

  const movieCount = (films || []).filter((f: MediaItem) => f.media_type === 'movie').length;
  const tvCount = (films || []).filter((f: MediaItem) => f.media_type === 'tv').length;

  if (!films || films.length === 0) {
    return <PageLoader />;
  }

  return (
    <div className="min-h-screen bg-dark-950">
      <Header showSearch={false} />

      {/* Hero Section */}
      <section className="relative py-8 sm:py-12 lg:py-16 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-amber-600/10 via-dark-950 to-rose-600/10" />

        <div className="relative max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white
              transition-colors group mb-8"
          >
            <FontAwesomeIcon
              icon={faArrowLeft}
              className="transform group-hover:-translate-x-1 transition-transform"
            />
            <span>Back</span>
          </button>

          <div className="text-center mb-10">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black mb-4
              animate-fade-in">
              Actor's <span className="text-gradient">Filmography</span>
            </h1>
            <p className="text-gray-400 text-lg animate-fade-in" style={{ animationDelay: '100ms' }}>
              Explore their complete body of work
            </p>
          </div>

          {/* Stats & Filter */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12
            animate-fade-in" style={{ animationDelay: '200ms' }}>
            {/* Stats */}
            <div className="flex gap-8">
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-gradient">{films.length}</p>
                <p className="text-gray-500 text-sm">Total Credits</p>
              </div>
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-rose-400">{movieCount}</p>
                <p className="text-gray-500 text-sm">Movies</p>
              </div>
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-cyan-400">{tvCount}</p>
                <p className="text-gray-500 text-sm">TV Shows</p>
              </div>
            </div>

            {/* Filter */}
            <div className="flex gap-2">
              {[
                { value: 'all', label: 'All', count: films.length },
                { value: 'movie', label: 'Movies', count: movieCount, icon: faFilm },
                { value: 'tv', label: 'TV', count: tvCount, icon: faTv },
              ].map((item) => (
                <button
                  key={item.value}
                  onClick={() => setFilter(item.value as 'all' | 'movie' | 'tv')}
                  className={`
                    px-4 py-2 rounded-full font-medium text-sm
                    transition-all duration-300
                    ${filter === item.value
                      ? 'bg-gradient-to-r from-primary-500 to-rose-500 text-white'
                      : 'bg-dark-800 text-gray-400 hover:text-white hover:bg-dark-700'
                    }
                  `}
                >
                  {item.icon && <FontAwesomeIcon icon={item.icon} className="mr-2" />}
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Selected Film Player */}
      {selectedFilm && (
        <section className="max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 
          mb-12 animate-fade-in">
          <div className="relative rounded-2xl overflow-hidden 
            bg-dark-900 shadow-2xl shadow-primary-500/10">
            {/* Player */}
            <div className="aspect-video">
              <iframe
                src={`${VIDEO_SOURCE_URL}/${selectedFilm.media_type}/${selectedFilm.id}`}
                title={`Watch ${getMediaTitle(selectedFilm)}`}
                className="w-full h-full"
                allowFullScreen
                allow="autoplay; encrypted-media; fullscreen"
              />
            </div>

            {/* Film Info */}
            <div className="p-4 sm:p-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold mb-2">
                    {getMediaTitle(selectedFilm)}
                  </h2>
                  <div className="flex items-center gap-4 text-sm text-gray-400">
                    {selectedFilm.vote_average > 0 && (
                      <span className="flex items-center gap-1 text-primary-400">
                        <FontAwesomeIcon icon={faStar} />
                        {selectedFilm.vote_average.toFixed(1)}
                      </span>
                    )}
                    {getMediaYear(selectedFilm) && (
                      <span>
                        {getMediaYear(selectedFilm)}
                      </span>
                    )}
                    <span className={`px-2 py-0.5 rounded text-xs font-medium
                      ${selectedFilm.media_type === 'movie'
                        ? 'bg-rose-500/20 text-rose-400'
                        : 'bg-cyan-500/20 text-cyan-400'
                      }`}
                    >
                      {selectedFilm.media_type === 'movie' ? 'Movie' : 'TV Show'}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedFilm(null)}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20
                    transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              {selectedFilm.overview && (
                <p className="text-gray-400 text-sm sm:text-base line-clamp-3">
                  {selectedFilm.overview}
                </p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Films Grid */}
      <main className="max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 pb-20">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold">
            {filter === 'all' ? 'All Works' : filter === 'movie' ? 'Movies' : 'TV Shows'}
          </h2>
          <span className="text-gray-500">{filteredFilms.length} titles</span>
        </div>

        {filteredFilms.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 sm:gap-6">
            {filteredFilms.map((film: MediaItem, index: number) => (
              <div
                key={`${film.id}-${(film as any).credit_id || index}`}
                onClick={() => handleFilmSelect(film)}
                className="group cursor-pointer animate-fade-in"
                style={{ animationDelay: `${index * 30}ms` }}
              >
                <div className="relative aspect-poster rounded-xl overflow-hidden
                  bg-dark-800 shadow-lg
                  transform transition-all duration-300
                  group-hover:scale-105 group-hover:shadow-xl
                  ring-2 ring-transparent group-hover:ring-primary-500/50">
                  <img
                    src={film.poster_path
                      ? `${IMAGE_SIZES.poster.medium}${film.poster_path}`
                      : '/placeholder-poster.jpg'
                    }
                    alt={getMediaTitle(film)}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />

                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent 
                    opacity-60 group-hover:opacity-80 transition-opacity" />

                  {/* Play button */}
                  <div className="absolute inset-0 flex items-center justify-center
                    opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="p-3 rounded-full bg-primary-500 shadow-lg">
                      <FontAwesomeIcon icon={faPlay} className="text-white ml-0.5" />
                    </div>
                  </div>

                  {/* Rating */}
                  {film.vote_average > 0 && (
                    <div className="absolute top-2 left-2 flex items-center gap-1
                      px-2 py-1 rounded bg-black/60 backdrop-blur-sm text-xs">
                      <FontAwesomeIcon icon={faStar} className="text-primary-400" />
                      <span>{film.vote_average.toFixed(1)}</span>
                    </div>
                  )}

                  {/* Media type badge */}
                  <div className={`absolute top-2 right-2 p-1.5 rounded
                    ${film.media_type === 'movie'
                      ? 'bg-rose-500/80'
                      : 'bg-cyan-500/80'
                    }`}>
                    <FontAwesomeIcon
                      icon={film.media_type === 'movie' ? faFilm : faTv}
                      className="text-white text-xs"
                    />
                  </div>

                  {/* Title on hover */}
                  <div className="absolute bottom-0 left-0 right-0 p-3
                    transform translate-y-full group-hover:translate-y-0
                    transition-transform duration-300">
                    <h3 className="text-white font-semibold text-sm line-clamp-2">
                      {getMediaTitle(film)}
                    </h3>
                    {(film as any).character && (
                      <p className="text-gray-400 text-xs mt-1 line-clamp-1">
                        as {(film as any).character}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg">No {filter} found</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default ActorDetails;
